import React from 'react';
import PropTypes from 'prop-types';
import Card from '../../../layout/Card/Card';
import { formatCurrency, formatPercentage } from '../../../../utils/formatters';
import styles from './SimulationResult.module.css';
import ResultIndicator from './ResultIndicator/ResultIndicator';
import FormRow from '../../../form/FormRow/FormRow';
import FormColumn from '../../../form/FormColumn/FormColumn';

const SimulationResult = ({ result, isMRV, pagadoria }) => {
  if (!result) return null;

  // Carta de Crédito: parcela tipo 7 substitui o ato quando aplicada; se o valor da carta não
  // cobrir o ato integralmente, uma parcela de Sinal complementar (atoComplementar) é criada.
  const parcelaCartaDeCredito = result.proposta.parcelas.find(p => p.tipo === 7);
  const parcelaAtoComplementar = result.proposta.parcelas.find(p => p.atoComplementar);

  return (
    <div id="resultadoSimulacao" className={styles.simulationResult}>
      <h3 className={styles.simulationTitle}>Resultado da Simulação</h3>
      <Card title="Resumo da Proposta">
        <FormRow>
          <FormColumn cols={3}>
            <ResultIndicator
              label="Valor da Proposta"
              value={formatCurrency(result.valorProposta)}
            />
          </FormColumn>
          <FormColumn cols={3}>
            <ResultIndicator
              label="Valor Financiado"
              value={formatCurrency(result.proposta.valorFinanciado)}
            />
          </FormColumn>
          <FormColumn cols={3}>
            <ResultIndicator
              label={pagadoria ? "Sinal Ato +Comissão" : "Valor do Sinal"}
              value={formatCurrency(result.proposta.valorSinal)}
            />
          </FormColumn>
          <FormColumn cols={3}>
            <ResultIndicator
              label={`Carteira ${isMRV ? 'MRV' : 'Sensia'}`}
              value={`${formatCurrency(result.proposta.valorTotalNominalParcelasMensais)} (${result.proposta.quantidadeParcelasMensais}x)`}
            />
          </FormColumn>
          <FormColumn cols={3}>
            <ResultIndicator
              label="Resgate FGTS"
              value={formatCurrency(result.proposta.fgts)}
            />
          </FormColumn>
          <FormColumn cols={3}>
            <ResultIndicator
              label="Subsídio"
              value={formatCurrency(result.proposta.subsidio)}
            />
          </FormColumn>
         
          <FormColumn cols={3}>
            <ResultIndicator
              label="Comprometimento Renda"
              value={formatPercentage(result.proposta.comprometimentoRendaProposta)}
              colorStatus={result.statusParametrosRatingSelecionados?.corPercentualComprometimentoMaximoRenda}
            />
          </FormColumn>
          <FormColumn cols={3}>
            <ResultIndicator
              label="Risco Total"
              value={formatPercentage(result.riscoTotal)}
              colorStatus={result.statusParametrosRatingSelecionados?.corRiscoTotalMaximo}
            />
          </FormColumn>
          <FormColumn cols={3}>
            <ResultIndicator
              label="Risco Carteira"
              value={formatPercentage(result.proposta.riscoCarteira)}
              colorStatus={result.statusParametrosRatingSelecionados?.corRiscoCarteiraMaximo}
            />
          </FormColumn>
          <FormColumn cols={3}>
            <ResultIndicator
              label="Valor Bom Pagador"
              value={formatCurrency(result.proposta.valorTotalBomPagador)}
            />
          </FormColumn>
          <FormColumn cols={3}>
            <ResultIndicator
              label="Valor Cupom"
              value={formatCurrency(result.proposta.cupom.valor)}
            />
          </FormColumn>
          <FormColumn cols={3}>
            <ResultIndicator
              label="Valor Intermediária"
              value={formatCurrency(
                result.proposta.parcelas
                  .filter(parcela => parcela.tipo === 1)
                  .reduce((acc, parcela) => acc + (parseFloat(parcela.valorNominal) || 0), 0)
              )}
            />
          </FormColumn>
          {parcelaCartaDeCredito && (
            <FormColumn cols={3}>
              <ResultIndicator
                label="Valor Carta de Crédito"
                value={formatCurrency(parcelaCartaDeCredito.valorNominal)}
              />
            </FormColumn>
          )}
          {parcelaAtoComplementar && (
            <FormColumn cols={3}>
              <ResultIndicator
                label="Ato Complementar"
                value={formatCurrency(parcelaAtoComplementar.valorNominal)}
              />
            </FormColumn>
          )}
        </FormRow>
      </Card>

      {/* Tabela de cronograma de pagamentos */}
      <Card title="Cronograma de Pagamentos">
        <FormRow>
          <FormColumn cols={12}>
            <div className={styles.tableResponsive}>
              <table className={styles.table} id="tabelaParcelas">
                <thead>
                  <tr>
                    <th>Vencimento</th>
                    <th>Tipo</th>
                    <th>Valor Nominal (R$)</th>
                  </tr>
                </thead>
                <tbody>
                  {result.proposta.parcelas.map((parcela, index) => {
                    const uniqueKey = `parcela-${parcela.dataVencimento}-${parcela.tipo}-${parcela.valorNominal}-${index}`;
                    return (
                      <tr key={uniqueKey}>
                        <td>{parcela.dataFormatada}</td>
                        <td>{parcela.tipoDescricao}</td>
                        <td>{parcela.valorFormatado}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </FormColumn>
        </FormRow>
      </Card>
    </div>
  );
};

SimulationResult.propTypes = {
  result: PropTypes.shape({
    valorProposta: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    riscoTotal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    proposta: PropTypes.shape({
      valorFinanciado: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      valorSinal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      valorTotalNominalParcelasMensais: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      quantidadeParcelasMensais: PropTypes.number,
      fgts: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      subsidio: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      comprometimentoRendaProposta: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      riscoCarteira: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      valorTotalBomPagador: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      cupom: PropTypes.shape({
        valor: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      }),
      parcelas: PropTypes.arrayOf(PropTypes.shape({
        dataVencimento: PropTypes.string,
        dataFormatada: PropTypes.string,
        tipo: PropTypes.number,
        tipoDescricao: PropTypes.string,
        valorNominal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        valorFormatado: PropTypes.string,
        atoComplementar: PropTypes.bool
      }))
    }),
    statusParametrosRatingSelecionados: PropTypes.shape({
      corPercentualComprometimentoMaximoRenda: PropTypes.number,
      corRiscoTotalMaximo: PropTypes.number,
      corRiscoCarteiraMaximo: PropTypes.number
    })
  }).isRequired,
  isMRV: PropTypes.bool,
  pagadoria: PropTypes.bool
};

SimulationResult.defaultProps = {
  isMRV: false,
  pagadoria: false
};

export default SimulationResult;